/**
 * ChildrenSection.jsx - a record's "Children" (10/14/26): the records whose
 * Main project is this one, each with Remove (unlink), and "Add existing
 * projects..." to put records under it by hand - several at once, asking
 * before moving any that sit under another project. Every link is a save of
 * that record (linking.js), so the Activity log, Revert, live updates, the
 * edit check and the edit-conflict guard all apply. Main tab only.
 */
import { useState } from 'react';
import { fmt } from '../../../utils/format';
import { isProjectRow } from '../../../data/projectStatus';
import { ancestorIds, childrenOf, recordLabel } from '../../../data/projectHierarchy';
import ActiveClosedPill, { projectStatus } from '../ActiveClosedPill';
import { StatusPill } from '../../ui/StatusPill';
import RecordPickerDialog from './RecordPickerDialog';
import { setMainProject, setMainProjectOfMany } from './linking';

const btn = {
  background: '#fff',
  color: '#1B5E20',
  border: '1px solid #1B5E20',
  padding: '5px 12px',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};
const td = { fontSize: 12, padding: '7px 8px', borderBottom: '1px solid #F0F2EC', verticalAlign: 'middle' };

/** Props: record, all (every record), editable, saveProject, onOpen(id), onDone(message) */
export default function ChildrenSection({ record, all = [], editable = false, saveProject, onOpen, onDone }) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const kids = childrenOf(all, record.id);
  const kidIds = new Set(kids.map((r) => String(r.id)));
  const above = ancestorIds(all, record.id);
  // never itself, one of its own main projects (a loop), or one already under it
  const candidates = all.filter((r) => String(r.id) !== String(record.id) && !above.has(String(r.id)) && !kidIds.has(String(r.id)));

  const unlink = async (r) => {
    setBusyId(r.id);
    setError('');
    try {
      const res = await setMainProject(saveProject, r, null);
      if (!res || !res.ok) setError(`${recordLabel(r)}: ${(res && res.body && res.body.error) || 'could not save'}`);
      else if (onDone) onDone(`${recordLabel(r)} removed from this project`);
    } finally {
      setBusyId(null);
    }
  };
  const add = async (picked) => {
    const out = await setMainProjectOfMany(saveProject, picked, record.id);
    if (out.done.length && onDone) onDone(`${out.done.length} record${out.done.length === 1 ? '' : 's'} put under this project`);
    return out;
  };

  return (
    <section
      data-testid="children-section"
      style={{ background: '#fff', border: '1px solid #E5E9DD', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 800, color: '#1B5E20', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Children ({kids.length})
        </h3>
        {editable && (
          <button type="button" style={btn} onClick={() => setAdding(true)}>
            ＋ Add existing projects…
          </button>
        )}
      </div>
      {kids.length === 0 ? (
        <div style={{ fontSize: 12, color: '#777' }}>
          Nothing sits under this record.{' '}
          {editable ? 'Add existing projects, or make a new PO line or sub-project from the buttons above.' : ''}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Children">
            <tbody>
              {kids.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, cursor: onOpen ? 'pointer' : undefined }} onClick={() => onOpen && onOpen(String(r.id))}>
                    <div style={{ fontWeight: 700 }}>{recordLabel(r)}</div>
                    <div style={{ fontSize: 10.5, color: '#777' }}>{[r.customer, r.description].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(r.poValue ?? r.po_value)}
                  </td>
                  <td style={td}>{isProjectRow(r) ? <ActiveClosedPill value={projectStatus(r)} /> : <StatusPill status={r.status} />}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => unlink(r)}
                        disabled={busyId === r.id}
                        aria-label={`Remove ${recordLabel(r)} from this project`}
                        style={{ ...btn, color: '#B71C1C', borderColor: '#E7B4B4', padding: '3px 10px' }}
                      >
                        {busyId === r.id ? 'Saving...' : 'Remove'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 8, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
          {error}
        </div>
      )}
      {adding && (
        <RecordPickerDialog
          title="Add existing projects"
          intro={
            <>
              Pick the records that sit under <b>{recordLabel(record)}</b>. Each one is saved with this record as its Main project.
            </>
          }
          candidates={candidates}
          all={all}
          multiple
          customer={record.customer}
          targetId={record.id}
          confirmMoves
          confirmLabel="Put under this project"
          onPick={add}
          onClose={() => setAdding(false)}
        />
      )}
    </section>
  );
}
